import {useDeviceLocale} from '~/composables/useDeviceLocale';

/**
 * Display names for sports. One job today: "Football" reads as "Soccer" for
 * readers in the countries where football primarily means another code
 * (US, CA, AU, NZ). DISPLAY-ONLY — the stored name, slugs, routes and
 * structured data all stay "Football"; only visible labels pass through here.
 *
 * Locale comes from the device (useDeviceLocale — Accept-Language on the
 * server, navigator.language on the client, so SSR and hydration agree),
 * NOT the nuxt-i18n locale, which only ships English variants.
 *
 * `sportDisplayName` is a plain auto-imported function so templates can call
 * it directly; outside a Nuxt instance (late event handlers) it falls back to
 * navigator.language.
 */
const SOCCER_REGIONS = new Set(['US', 'CA', 'AU', 'NZ']);

// Only an EXPLICIT region counts ("en-AU" yes, bare "en" no) — inferring a
// region for plain "en" would rename football for the product's historic
// default-English audience.
const regionOf = (tag) => {
  if (!tag) return null;
  const parts = String(tag).split(/[-_]/).filter(Boolean);
  const region = parts.slice(1).find((p) => /^[A-Za-z]{2}$/.test(p));
  return region ? region.toUpperCase() : null;
};

const deviceLocaleTag = () => {
  try {
    return useDeviceLocale().value;
  } catch {
    return (typeof navigator !== 'undefined' && navigator.language) || null;
  }
};

export function sportDisplayName(sportOrName) {
  const name = typeof sportOrName === 'string' ? sportOrName : (sportOrName?.name ?? '');
  if (name.trim().toLowerCase() !== 'football') return name;
  return SOCCER_REGIONS.has(regionOf(deviceLocaleTag())) ? 'Soccer' : name;
}

export function useSportDisplay() {
  return {sportDisplayName};
}
